import React, { useState } from 'react';
import DataGrid from './DataGrid';
import ChartPanel from './ChartPanel';
import { exportCsv, useTableData } from '../hooks/useDbExplorer';

const TableBrowser = ({ schema, table, picker }) => {
  const td = useTableData(schema, table);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState('grid'); // 'grid' | 'chart'

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCsv({
        schema,
        table,
        filters: td.filters,
        sort: td.sort,
        globalFilter: td.globalFilter,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      window.alert(`Export failed: ${detail}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="db-explorer-subtoolbar">
        {picker}

        <button
          type="button"
          className="btn btn-sm btn-outline-light db-explorer-icon-btn"
          onClick={td.refresh}
          disabled={td.loading}
          title="Refresh"
        >
          ⟳
        </button>

        <div className="db-explorer-global-filter">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Filter in any column"
            value={td.globalFilter}
            onChange={(e) => td.setGlobal(e.target.value)}
          />
        </div>

        <div className="btn-group btn-group-sm db-explorer-view-toggle" role="group">
          <button
            type="button"
            className={`btn btn-sm ${view === 'grid' ? 'btn-secondary' : 'btn-outline-light'}`}
            onClick={() => setView('grid')}
          >
            Grid
          </button>
          <button
            type="button"
            className={`btn btn-sm ${view === 'chart' ? 'btn-secondary' : 'btn-outline-light'}`}
            onClick={() => setView('chart')}
            disabled={td.columns.length === 0}
          >
            Chart
          </button>
        </div>

        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={handleExport}
          disabled={td.loading || exporting || td.rows.length === 0}
          title="Export current view to CSV"
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>

        <div className="db-explorer-row-info">
          {td.total != null ? (
            <span>
              {td.pageInfo.showingFrom.toLocaleString()}–{td.pageInfo.showingTo.toLocaleString()} of {td.total.toLocaleString()}
            </span>
          ) : (
            <span>{td.rows.length.toLocaleString()} rows</span>
          )}
        </div>

        <div className="db-explorer-pager">
          <button type="button" className="btn btn-sm btn-outline-light" onClick={td.goFirst} disabled={td.pageInfo.offset === 0}>«</button>
          <button type="button" className="btn btn-sm btn-outline-light" onClick={td.goPrev} disabled={td.pageInfo.offset === 0}>‹</button>
          <button type="button" className="btn btn-sm btn-outline-light" onClick={td.goNext} disabled={td.total != null && td.pageInfo.offset + td.pageInfo.limit >= td.total}>›</button>
          <button type="button" className="btn btn-sm btn-outline-light" onClick={td.goLast} disabled={td.total == null}>»</button>
        </div>
      </div>

      {td.error && (
        <div className="alert alert-warning db-explorer-alert">{td.error}</div>
      )}

      <div className="db-explorer-body">
        {view === 'grid' ? (
          <DataGrid
            columns={td.columns}
            rows={td.rows}
            filters={td.filters}
            sort={td.sort}
            loading={td.loading}
            onSort={td.toggleSort}
            onFilter={td.setColumnFilter}
            offset={td.pageInfo.offset}
          />
        ) : (
          <ChartPanel
            schema={schema}
            table={table}
            columns={td.columns}
            columnTypes={td.columnTypes}
            filters={td.filters}
            sort={td.sort}
            globalFilter={td.globalFilter}
          />
        )}
      </div>
    </>
  );
};

export default TableBrowser;
